import { BaseClient } from 'src/common-services/clients/base-client'
import {
  AMCensusBlockValue,
  AMDateValue,
  AMDayValue,
  AMHourValue,
  AMLocationRankByBrandListResponse,
  AMMobilityDateRange,
  AMMobilityLocationAvailableRequestProviders,
  AMMobilityLocationDwellTimeRequest,
  AMMobilityLocationProvider,
  AMMobilityLocationProviderIdRequest,
  AMMobilityLocationRankBrandRequest,
  AMMobilityLocationRankByBrandChartRequest,
  AMMobilityLocationRankByBrandHistogramRequest,
  AMMobilityLocationRankByBrandOverTimeRequest,
  AMMobilityLocationRankByBrandRequest,
  AMMobilityLocationRankByNaicsChartRequest,
  AMMobilityLocationRankByNaicsListRequest,
  AMMobilityLocationRankByNaicsRequest,
  AMMobilityLocationRankChart,
  AMMobilityLocationRankHistogram,
  AMMobilityLocationRankList,
  AMMobilityLocationRankPosition,
  AMMobilityLocationVisitorsByHomeRequest,
  AMMobilityLocationVisitorsRequest,
  AMMobilityLocationVisitsByDayRequest,
  AMMobilityLocationVisitsByHomeDateRangeRequest,
  AMMobilityLocationVisitsByHomeDateRangeRespense,
  AMMobilityLocationVisitsByHomeRequest,
  AMMobilityLocationVisitsByHourRequest,
  AMMobilityLocationVisitsRequest,
  AMMobilityPolygonAllRankRequest,
  AMMobilityPolygonAllRankResponse,
  AMMobilityPolygonTopLocation,
  AMMobilityPolygonTopLocationsRequest,
  AMMobilityPolygonVisitorsRequest,
  AMMobilityPolygonVisitsByDayRequest,
  AMMobilityPolygonVisitsByHourRequest,
  AMMobilityPolygonVisitsRequest,
  AMMobilityShoppingCenterLocations,
  AMMobilityShoppingCenterLocationsRequest,
  BoundaryType,
  amMobilityDataProviderEnum,
  AMMobilityUnacastLocationDwellTime,
} from 'src/common-services/types/alphamap'
import { YYYYMMDDDateString } from 'src/common-services/types/custom-footfall-types'

export class MobilityClient extends BaseClient {
  getDateRange(): Promise<AMMobilityDateRange> {
    return this.call<AMMobilityDateRange>('/v2/mobility/date-range', 'GET')
  }

  getValidProviderEndDate(
    provider: amMobilityDataProviderEnum,
    locationIds: string[]
  ): Promise<YYYYMMDDDateString | undefined> {
    return this.call(`/v2/mobility/valid-provider-end-date`, 'POST', {
      data: { provider, locationIds },
    })
  }

  getValidAdvanEndDate(locationIds: string[]): Promise<YYYYMMDDDateString | undefined> {
    return this.call(`/v2/mobility/valid-advan-end-date`, 'POST', {
      data: { locationIds },
    })
  }

  getValidVisitsEndDate(locationIds: string[]): Promise<YYYYMMDDDateString | undefined> {
    return this.call(`/v2/mobility/valid-visits-end-date`, 'POST', {
      data: { locationIds },
    })
  }

  getTTADateRange(request: AMMobilityLocationProviderIdRequest): Promise<AMMobilityDateRange> {
    return this.call<AMMobilityDateRange>(`/v2/mobility/location/${request.id}/tta-date-range`, 'POST', {
      data: request,
    })
  }

  getLocationVisits(request: AMMobilityLocationVisitsRequest): Promise<AMDateValue[]> {
    return this.call<AMDateValue[]>(`/v2/mobility/location/${request.id}/visits/${request.provider}`, 'POST', {
      data: request,
    })
  }

  getLocationVisitors(request: AMMobilityLocationVisitorsRequest): Promise<AMDateValue[]> {
    return this.call<AMDateValue[]>(`/v2/mobility/location/${request.id}/visitors/${request.provider}`, 'POST', {
      data: request,
    })
  }

  getLocationAvailableProviders(
    request: AMMobilityLocationAvailableRequestProviders
  ): Promise<AMMobilityLocationProvider[]> {
    return this.call<AMMobilityLocationProvider[]>(`/v2/mobility/location/${request.id}/providers`, 'GET')
  }

  getLocationVisitsByHour(request: AMMobilityLocationVisitsByHourRequest): Promise<AMHourValue[]> {
    return this.call<AMHourValue[]>(`/v2/mobility/location/${request.id}/visits-by-hour`, 'POST', {
      data: request,
    })
  }

  getLocationVisitsByDay(request: AMMobilityLocationVisitsByDayRequest): Promise<AMDayValue[]> {
    return this.call<AMDayValue[]>(`/v2/mobility/location/${request.id}/visits-by-day`, 'POST', {
      data: request,
    })
  }

  getLocationVisitorsByHome(request: AMMobilityLocationVisitorsByHomeRequest): Promise<AMCensusBlockValue[]> {
    return this.call<AMCensusBlockValue[]>(`/v2/mobility/location/${request.id}/visitors-by-home`, 'POST', {
      data: request,
    })
  }

  getLocationVisitsByHome(request: AMMobilityLocationVisitsByHomeRequest): Promise<AMCensusBlockValue[]> {
    return this.call<AMCensusBlockValue[]>(`/v2/mobility/location/${request.id}/visits-by-home`, 'POST', {
      data: request,
    })
  }

  getLocationRankByNaicsPosition(
    request: AMMobilityLocationRankByNaicsRequest
  ): Promise<AMMobilityLocationRankPosition> {
    return this.call<AMMobilityLocationRankPosition>(
      `/v2/mobility/location/${request.id}/rank/by-naics/position/${request.provider}`,
      'POST',
      {
        data: request,
      }
    )
  }

  getLocationRankByBrandPosition(
    request: AMMobilityLocationRankByBrandRequest
  ): Promise<AMMobilityLocationRankPosition> {
    return this.call<AMMobilityLocationRankPosition>(
      `/v2/mobility/location/${request.id}/rank/by-brand/position/${request.provider}`,
      'POST',
      {
        data: request,
      }
    )
  }

  getLocationRankByBrandHistogram(
    request: AMMobilityLocationRankByBrandHistogramRequest
  ): Promise<AMMobilityLocationRankHistogram[]> {
    return this.call<AMMobilityLocationRankHistogram[]>(
      `/v2/mobility/location/${request.id}/rank/by-brand/histogram/${request.provider}`,
      'POST',
      {
        data: request,
      }
    )
  }

  getLocationRankByBrandChart(
    request: AMMobilityLocationRankByBrandChartRequest
  ): Promise<AMMobilityLocationRankChart[]> {
    return this.call<AMMobilityLocationRankChart[]>(
      `/v2/mobility/location/${request.id}/rank/by-brand/chart/${request.provider}`,
      'POST',
      {
        data: request,
      }
    )
  }

  getLocationRankByNaicsChart(
    request: AMMobilityLocationRankByNaicsChartRequest
  ): Promise<AMMobilityLocationRankChart[]> {
    return this.call<AMMobilityLocationRankChart[]>(
      `/v2/mobility/location/${request.id}/rank/by-naics/chart/${request.provider}`,
      'POST',
      {
        data: request,
      }
    )
  }

  getLocationRankByBrandList(
    request: AMMobilityLocationRankByBrandRequest
  ): Promise<AMLocationRankByBrandListResponse> {
    return this.call<AMLocationRankByBrandListResponse>(
      `/v2/mobility/location/${request.id}/rank/by-brand/list/${request.provider}`,
      'POST',
      {
        data: request,
      }
    )
  }

  getLocationRankByNaicsList(
    request: AMMobilityLocationRankByNaicsListRequest
  ): Promise<AMLocationRankByBrandListResponse> {
    return this.call<AMLocationRankByBrandListResponse>(
      `/v2/mobility/location/${request.id}/rank/by-naics/list/${request.provider}`,
      'POST',
      {
        data: request,
      }
    )
  }

  getLocationRankBrandList(request: AMMobilityLocationRankBrandRequest): Promise<AMMobilityLocationRankList[]> {
    return this.call<AMMobilityLocationRankList[]>(
      `/v2/mobility/location/rank/by-brand/${request.brandId}/list/${request.provider}`,
      'POST',
      {
        data: {
          date: request.date,
        },
      }
    )
  }

  getLocationRankByBrandPositionOverTime(
    request: AMMobilityLocationRankByBrandOverTimeRequest
  ): Promise<AMDateValue[]> {
    return this.call<AMDateValue[]>(
      `/v2/mobility/location/${request.id}/rank/by-brand/position-over-time/${request.provider}`,
      'POST',
      {
        data: request,
      }
    )
  }

  getLocationRankByNaicsPositionOverTime(
    request: AMMobilityLocationRankByBrandOverTimeRequest
  ): Promise<AMDateValue[]> {
    return this.call<AMDateValue[]>(
      `/v2/mobility/location/${request.id}/rank/by-naics/position-over-time/${request.provider}`,
      'POST',
      {
        data: request,
      }
    )
  }

  getLocationDwellTimesById(request: AMMobilityLocationDwellTimeRequest) {
    return this.call<AMMobilityUnacastLocationDwellTime>(`/v2/mobility/location/${request.id}/dwell-times`, 'POST', {
      data: request,
    })
  }

  getPolygonTopLocations(request: AMMobilityPolygonTopLocationsRequest): Promise<AMMobilityPolygonTopLocation[]> {
    return this.call<AMMobilityPolygonTopLocation[]>(`/v2/mobility/polygon/top-locations`, 'POST', {
      data: request,
    })
  }

  getPolygonAllRank(request: AMMobilityPolygonAllRankRequest): Promise<AMMobilityPolygonAllRankResponse[]> {
    return this.call<AMMobilityPolygonAllRankResponse[]>(`/v2/mobility/polygon/all-rank`, 'POST', {
      data: request,
    })
  }

  getPolygonVisits(request: AMMobilityPolygonVisitsRequest): Promise<AMDateValue[]> {
    return this.call<AMDateValue[]>(`/v2/mobility/polygon/visits`, 'POST', {
      data: request,
    })
  }

  getPolygonVisitors(request: AMMobilityPolygonVisitorsRequest): Promise<AMDateValue[]> {
    return this.call<AMDateValue[]>(`/v2/mobility/polygon/visitors`, 'POST', {
      data: request,
    })
  }

  getPolygonVisitsByHour(request: AMMobilityPolygonVisitsByHourRequest): Promise<AMHourValue[]> {
    return this.call<AMHourValue[]>(`/v2/mobility/polygon/visits-by-hour`, 'POST', {
      data: request,
    })
  }

  getPolygonVisitsByDay(request: AMMobilityPolygonVisitsByDayRequest): Promise<AMDayValue[]> {
    return this.call<AMDayValue[]>(`/v2/mobility/polygon/visits-by-day`, 'POST', {
      data: request,
    })
  }

  getShoppingCenterLocations(
    request: AMMobilityShoppingCenterLocationsRequest
  ): Promise<AMMobilityShoppingCenterLocations[]> {
    return this.call<AMMobilityShoppingCenterLocations[]>(
      `/v2/mobility/shopping-center/${request.id}/locations`,
      'POST',
      {
        data: request,
      }
    )
  }

  getLocationVisitsByHomeDateRange(
    request: AMMobilityLocationVisitsByHomeDateRangeRequest
  ): Promise<AMMobilityLocationVisitsByHomeDateRangeRespense> {
    return this.call<any>(`/v2/mobility/location/${request.id}/visits-by-home-date-range`, 'POST', {
      data: request,
    })
  }

  getAvailableBoundaries(locationId: string): Promise<BoundaryType[]> {
    return this.call<BoundaryType[]>(`/v2/mobility/location/${locationId}/available-boundaries`, 'GET')
  }
}
